import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, Search } from 'lucide-react';
import { BRANDS } from '../../data/brands';
import { NAV_ITEMS, type NavItem } from '../../data/navigation';
import { BrandLogo } from '../BrandLogo';
import { MobileNav } from './MobileNav';
export function Header() {
  const {
    pathname
  } = useLocation();
  const brand = BRANDS.find(b => `/${b.id}` === pathname);
  const [menuOpen, setMenuOpen] = useState(false);
  return <>
      {brand ? <BrandHeader brandId={brand.id} onOpenMenu={() => setMenuOpen(true)} data-id="element-130" data-mp="i0ixz3jdqcnq" /> : <GroupHeader onOpenMenu={() => setMenuOpen(true)} data-id="element-131" data-mp="vro1igpvbp3c" />}
      <MobileNav open={menuOpen} onClose={() => setMenuOpen(false)} data-id="element-132" data-mp="r060xtrrbqiw" />
    </>;
}

/** Glava skupine — polni glavni meni (domača stran in splošne podstrani) */
function GroupHeader({
  onOpenMenu
}: {
  onOpenMenu: () => void;
}) {
  const [openLabel, setOpenLabel] = useState<string | null>(null);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpenLabel(null);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  return <header className="sticky top-0 z-50 w-full bg-aw-navy text-white" data-id="element-133" data-mp="gmjaj2k7a4qu">
      <div className="mx-auto flex h-[68px] w-full max-w-[1360px] items-center gap-8 px-5 lg:px-10" data-id="element-134" data-mp="lkbc6oand588">
        <Link to="/" className="font-heading text-2xl tracking-tight" data-id="element-135" data-mp="3k2gn1i5u3osc">
          Auto<span className="font-bold" data-id="element-136" data-mp="3k4jocycft4cd">Wallis</span>
        </Link>

        <nav aria-label="Glavna navigacija" className="hidden h-full flex-1 xl:block" onMouseLeave={() => setOpenLabel(null)} data-id="element-137" data-mp="3p1tkewuc0e96">
          <ul className="flex h-full items-stretch" data-id="element-138" data-mp="1x3ou0zj0pi0">
            {NAV_ITEMS.map(item => <li key={item.label} className="relative flex" data-id="element-139" data-mp="19aoxzx2n27ud">
                <NavEntry item={item} open={openLabel === item.label && item.kind !== 'flat'} onOpen={() => setOpenLabel(item.kind === 'flat' ? null : item.label)} onClose={() => setOpenLabel(null)} data-id="element-140" data-mp="19c8glubrouzb" />
              </li>)}
          </ul>
        </nav>

        <div className="ml-auto flex items-center gap-1" data-id="element-141" data-mp="3jdbdfpv7zxcp">
          <button type="button" aria-label="Iskanje" className="flex h-11 w-11 items-center justify-center text-white/90 transition-colors hover:text-white" data-id="element-142" data-mp="3jap6nsg5llvj">
            <Search className="h-5 w-5" data-id="element-143" data-mp="2mmss0t4a10my" />
          </button>
          <button type="button" aria-label="Odpri meni" onClick={onOpenMenu} className="flex h-11 w-11 items-center justify-center text-white xl:hidden" data-id="element-144" data-mp="2modlsgirofjr">
            <Menu className="h-6 w-6" data-id="element-145" data-mp="2vle3k4bbzfe8" />
          </button>
        </div>
      </div>
    </header>;
}

/**
 * Glava strani znamke — v prvi vlogi je znamka, meni AutoWallis se umakne
 * v hamburger, desno pa sta znak znamke in prehod na vse znamke.
 */
function BrandHeader({
  brandId,
  onOpenMenu
}: {
  brandId: string;
  onOpenMenu: () => void;
}) {
  const brand = BRANDS.find(b => b.id === brandId);
  if (!brand) return null;
  return <header className="absolute inset-x-0 top-0 z-50 w-full text-white" data-id="element-146" data-mp="9gcm68863bis">
      <div className="mx-auto flex h-[72px] w-full max-w-[1360px] items-center px-5 lg:px-10" data-id="element-147" data-mp="9e9kus1kdvyr">
        <div className="flex items-center gap-3" data-id="element-148" data-mp="ed2x6h547gxc">
          <button type="button" aria-label="Odpri meni AutoWallis" onClick={onOpenMenu} className="flex h-11 w-11 items-center justify-center text-white transition-opacity hover:opacity-80" data-id="element-149" data-mp="125tb2vwkwaa9">
            <Menu className="h-6 w-6" data-id="element-150" data-mp="s8yc6bwe87vg" />
          </button>
          <Link to="/" className="hidden font-heading text-xl tracking-tight sm:block" data-id="element-151" data-mp="x4do2yaubtp8">
            Auto<span className="font-bold" data-id="element-152" data-mp="3cvrqcstv6qw0">Wallis</span>
          </Link>
        </div>

        <div className="ml-auto flex h-[72px] items-stretch" data-id="element-153" data-mp="3hw62on2i0mzu">
          <span className="flex items-center justify-center border-b-2 border-white px-6 sm:border-x sm:border-b-2 sm:border-x-white/25" data-id="element-154" data-mp="3htjkzulw8r3w">
            <BrandLogo brand={brand} color="white" size={24} data-id="element-155" data-mp="3seknl87mza61" />
          </span>
          <Link to="/" className="hidden items-center px-6 text-[12px] font-semibold uppercase tracking-[0.1em] text-white/90 transition-colors hover:text-white sm:flex" data-id="element-156" data-mp="3oczfe061rk26">
            Naše blagovne znamke
          </Link>
          <button type="button" aria-label="Iskanje" className="flex items-center justify-center px-5 text-white/90 transition-colors hover:text-white sm:border-l sm:border-white/25" data-id="element-157" data-mp="jy820d8r74sd">
            <Search className="h-5 w-5" data-id="element-158" data-mp="5z8bf01okftk" />
          </button>
        </div>
      </div>
    </header>;
}
function NavEntry({
  item,
  open,
  onOpen,
  onClose
}: {
  item: NavItem;
  open: boolean;
  onOpen: () => void;
  onClose: () => void;
}) {
  const entries = item.kind === 'brands' ? BRANDS.map(b => ({
    label: b.name,
    to: `/${b.id}`
  })) : (item.children ?? []).map(label => ({
    label,
    to: null as string | null
  }));
  if (item.kind === 'flat') {
    return <a href="#" onMouseEnter={onOpen} className="flex items-center px-3.5 text-[12px] font-semibold uppercase tracking-[0.1em] text-white/85 transition-colors hover:text-white" data-id="element-159" data-mp="2liiqqwuy45q8">
        {item.label}
      </a>;
  }
  return <>
      <button type="button" aria-expanded={open} aria-haspopup="true" onMouseEnter={onOpen} onFocus={onOpen} onClick={onOpen} className={`flex items-center px-3.5 text-[12px] font-semibold uppercase tracking-[0.1em] transition-colors ${open ? 'text-white' : 'text-white/85 hover:text-white'}`} data-id="element-160" data-mp="2zbi8z6m44e1h">
        {item.label}
      </button>

      {open && <ul onMouseEnter={onOpen} className="absolute left-0 top-full z-[60] min-w-[230px] bg-aw-panel py-3 shadow-xl" data-id="element-161" data-mp="1eyjaqtw1jpvr">
          {entries.map(entry => <li key={entry.label} data-id="element-162" data-mp="1bwo8zl5s2kjg">
              {entry.to ? <Link to={entry.to} onClick={onClose} className="block px-5 py-2.5 text-[12px] font-semibold uppercase tracking-[0.08em] text-white/90 transition-colors hover:text-aw-accent" data-id="element-163" data-mp="16wseqdmvu7h3">
                  {entry.label}
                </Link> : <a href="#" onClick={onClose} className="block px-5 py-2.5 text-[12px] font-semibold uppercase tracking-[0.08em] text-white/90 transition-colors hover:text-aw-accent" data-id="element-164" data-mp="1ur38dbw04f7t">
                  {entry.label}
                </a>}
            </li>)}
        </ul>}
    </>;
}